// Fleet → Releases. ONE register, and actions that open drawers.
//
// The first cut of this screen stacked seven sections on one route — build
// form, build history, rollout card, fleet, outstanding, install attempts,
// library — and that is a pile, not an information architecture. This console
// already solved the shape: Onboarding.js runs a registry and opens a Drawer
// for anything that is a flow or a detail, and it EXPORTS that grammar
// (Drawer, EntityDrawer, RvCard, RvRow) precisely so siblings use it rather
// than reinvent it — Review.js and Tenants.js already do. The BM-precedent law
// says never invent a pattern this console has solved. I invented one anyway.
//
// AND THE OTHER HALF, from Ledger.js: "tabs hide exactly the adjacency that
// carries the answer". Version, power state and an outstanding install intent
// are three facts about ONE cab, and the operator's real question — "why is
// this one still on vc33?" — is answered by seeing them together: on vc33 ·
// asleep 9h · pending vc35, 2 tries, DNS. Splitting those across three tables,
// which the first cut did, hides the answer exactly as Ledger warns. One row.
//
// So: one table. Selecting rows arms the actions. Build, Roll out and History
// are drawers, because each is a flow or an archive — not something you stare
// at while working the fleet.
//
// 3K discipline: the register opens as a WORKLIST — behind first, unknown
// next — never a census, and every list is server-limited.

import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHead, Tiles, DataTable, Pill, Loading, ErrorState, Button, Empty } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
// ONE grammar, imported — never re-implemented (the BM-precedent law).
import { Drawer, EntityDrawer, RvCard, RvRow } from './Onboarding';
// EntityDrawer renders <sec.Icon/> for every section — the prop is required,
// not optional, and omitting it renders undefined as a component.
import { ListChecks, Hammer, Library } from 'lucide-react';
// .drawer is `display:flex; flex-direction:column` with NO overflow, so a tall
// child lays the footer out below the viewport and its button becomes
// unreachable — wheel, End and scrollIntoView all do nothing, because a fixed
// element's overflow contributes no document scroll. Measured: Send went
// off-screen at 7 of 22 devices, and Start build at zero selection on any
// viewport under ~1015px. paneScroll is what EntityDrawer already uses;
// secBody is NOT a substitute, it clips the cards instead of scrolling them.
import ds from './Onboarding.module.css';

const OUTCOME_TONE = { confirmed: 'grow', unreported: 'warn', unconfirmed: 'warn', failed: 'bad' };
const BUILD_TONE = { success: 'grow', failed: 'bad', building: 'pend', queued: 'warn', canceled: 'warn' };

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}
const pkgShort = (p) => (p || '').replace('com.galigali.enterprise.', '') || '—';

// ── The one piece of judgement on this screen ───────────────────────────────
// Separates the states a release operator must never confuse. ~70% of all dark
// windows over 6h on this fleet are power-idle, nightly, by design; a view that
// calls that BROKEN cries wolf four times a night. A device woken below the
// low-battery threshold IS a fault and runs flat in about 45 minutes.
function powerState(d) {
  if (!d.state_at) return { tone: 'pend', label: 'unknown', why: 'no state reading in the last 30 minutes' };
  if (d.draining_unplugged) {
    return { tone: 'bad', label: 'draining ' + d.battery_percent + '%',
      why: 'awake, unplugged and under 20% — runs flat in ~45 min unless the cab is started' };
  }
  if (d.power_idle) {
    const h = d.power_idle_since_ms ? Math.round((Date.now() - Number(d.power_idle_since_ms)) / 3600000) : null;
    return { tone: 'pend', label: h != null ? 'asleep ' + h + 'h' : 'asleep',
      why: 'power-idle' + (d.power_idle_trigger ? ' (' + d.power_idle_trigger + ')' : '')
         + (d.battery_percent != null ? ', battery ' + d.battery_percent + '%' : '')
         + ' — normal end-of-day behaviour, not a fault' };
  }
  // power_idle false with the screen OFF is not "awake", and a release creates
  // this state deliberately: the flags live in the agent's memory, so installing
  // a new agent on a PARKED cab resets power_idle, power_idle_trigger and
  // power_idle_since_ms to clean while the tablet is still off vehicle power and
  // the display still off. Measured on three cabs on 2026-10-03 after taking
  // vc36 — reported awake, screen off, discharging, not serving. The grace timer
  // re-arms and they go back to asleep within POWER_GRACE_MS (30 min; 238abw did
  // exactly that), so this is a settling window, not a fault — but calling it
  // "awake" in green is how a whole rollout wave looks healthy and serves
  // nothing. Only an explicit false counts; older devices report null.
  if (d.is_interactive === false) {
    return { tone: 'pend', label: 'screen off',
      why: 'not power-idle, but the display is off'
         + (d.is_charging ? ' while charging' : ' and unplugged')
         + (d.battery_percent != null ? ', battery ' + d.battery_percent + '%' : '')
         + ' — usual cause is an agent install clearing the power-idle flags on a '
         + 'parked cab; it re-arms within 30 min' };
  }
  return { tone: 'grow', label: d.is_charging ? 'awake · charging' : 'awake',
    why: d.battery_percent != null ? 'battery ' + d.battery_percent + '%' : '' };
}

const canTarget = (d, code) => code != null && String(d.agent_code == null ? '' : d.agent_code) !== String(code);

const note = (tone) => ({
  border: '1px solid var(--' + tone + '-line)', background: 'var(--' + tone + '-soft)',
  borderRadius: 8, padding: '10px 14px', marginBottom: 14,
  color: 'var(--' + tone + '-text)', fontSize: 13,
});
const input = {
  padding: '6px 8px', borderRadius: 6, border: '1px solid var(--line)',
  background: 'var(--paper)', color: 'var(--ink)', fontSize: 13,
};
// Scrolls. See the Onboarding.module.css import above for why this matters.
const PANE = ds.paneScroll;
// History's sections already render inside EntityDrawer's paneScroll.
const SECPAD = { padding: '14px 18px' };
// The rollout endpoint refuses more than this per request. The UI caps at the
// same number rather than letting a 3K select-all arrive as a 400.
const ROLLOUT_CAP = 250;

/* ── Drawer 1: start a build ────────────────────────────────────────────── */
function BuildDrawer({ builds, onClose, onStarted }) {
  const [branch, setBranch] = useState('main');
  const [buildType, setBuildType] = useState('debug');
  const [apps, setApps] = useState(['enterprise-agent']);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const opts = (builds.data && builds.data.data && builds.data.data.options) || {};
  const canDispatch = builds.data && builds.data.data && builds.data.data.canDispatch;
  const rows = (builds.data && builds.data.data && builds.data.data.rows) || [];

  const start = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post('/v2/releases/build', { branch, apps, buildType });
      const b = (r.data && r.data.data) || {};
      setMsg({ tone: 'grow', text: 'Build ' + String(b.id).slice(0, 8) + ' dispatched on ' + b.branch + '. Usually 6–9 minutes.' });
      builds.reload(); if (onStarted) onStarted();
    } catch (e) {
      setMsg({ tone: 'warn', text: (e && e.response && e.response.data && e.response.data.error) || e.message || 'Build failed to start' });
    } finally { setBusy(false); }
  };

  return (
    <Drawer
      eyebrow="Release"
      title="Build an APK"
      sub="Signs with the committed keystore, uploads to the OTA bucket, and lands a library row with a checksum."
      onClose={onClose}
      footer={<Button primary onClick={start} disabled={busy || !canDispatch || !apps.length}>
        {busy ? 'Dispatching…' : 'Start build'}
      </Button>}
    >
      <div className={PANE}>
        {canDispatch === false && (
          <div style={note('warn')}>
            This server has no <code>GITHUB_API_TOKEN</code>, so it cannot start a build — said here rather
            than failing when you press the button. Builds dispatch from production.
          </div>
        )}
        {msg && <div style={note(msg.tone)}>{msg.text}</div>}

        <RvCard title="What to build">
          <RvRow label="Branch">
            <input style={{ ...input, width: '100%' }} value={branch}
                   onChange={(e) => setBranch(e.target.value)} placeholder="main" aria-label="branch" />
          </RvRow>
          <RvRow label="Type">
            <select style={input} value={buildType} onChange={(e) => setBuildType(e.target.value)} aria-label="build type">
              {(opts.buildTypes || ['debug']).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </RvRow>
          <RvRow label="Apps" hint="enterprise-master is deliberately not buildable here: its debug manifest exports an unguarded receiver and its package id is the production one.">
            {(opts.apps || []).map((a) => (
              <label key={a} style={{ marginRight: 14, fontSize: 13 }}>
                <input type="checkbox" checked={apps.includes(a)}
                       onChange={(e) => setApps((prev) => (e.target.checked ? prev.concat([a]) : prev.filter((x) => x !== a)))} />{' '}
                {a.replace('enterprise-', '')}
              </label>
            ))}
          </RvRow>
        </RvCard>

        <RvCard title="Recent builds">
          {rows.length === 0 ? <Empty title="No builds yet">Nothing has been built.</Empty> : (
            <DataTable
              rows={rows.slice(0, 10)}
              getKey={(r) => r.id}
              initialSort={{ key: 'created_at', dir: 'desc' }}
              columns={[
                { key: 'status', label: 'Status', width: 92,
                  render: (r) => <Pill tone={BUILD_TONE[r.status] || 'pend'}>{r.status}</Pill> },
                { key: 'branch', label: 'Branch', mono: true },
                { key: 'landed', label: 'Landed', width: 120,
                  render: (r) => (r.landed
                    ? <span style={{ color: r.landed_with_checksum === r.landed ? 'var(--grow-text)' : 'var(--warn-text)' }}>
                        {r.landed} apk{r.landed > 1 ? 's' : ''}
                        {r.landed_with_checksum === r.landed ? ' ✓sha' : ' (' + r.landed_with_checksum + ' sha)'}
                      </span>
                    : '—') },
                { key: 'created_at', label: 'Started', width: 132, render: (r) => fmt(r.created_at) },
                // The reason belongs HERE, not only in History. This is the
                // table someone looks at seconds after pressing Start build,
                // and the most likely failure is now a version collision —
                // which says exactly what to do about it.
                { key: 'error_message', label: 'Why', wrap: true,
                  render: (r) => (r.error_message
                    ? <span style={{ color: 'var(--bad-text)' }}>{String(r.error_message).slice(0, 200)}</span>
                    : '—') },
              ]}
            />
          )}
        </RvCard>
      </div>
    </Drawer>
  );
}

/* ── Drawer 2: review and send ──────────────────────────────────────────── */
// A review step, not a one-click fleet action. This pushes ~78 MB to cabs over
// cellular; the operator sees the shape of the batch before committing to it.
function RolloutDrawer({ selected, devices, library, onClose, onDone }) {
  const [targetId, setTargetId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const target = useMemo(
    () => library.find((l) => String(l.id) === String(targetId)) || null, [library, targetId]);
  const chosen = useMemo(
    () => devices.filter((x) => selected.has(x.thing_name)), [devices, selected]);

  const reachable = chosen.filter((x) => x.state_at && !x.power_idle).length;
  const asleep = chosen.filter((x) => x.power_idle).length;
  const silent = chosen.filter((x) => !x.state_at).length;
  const alreadyOn = target ? chosen.filter((x) => !canTarget(x, target.version_code)).length : 0;

  const send = async () => {
    if (!target) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post('/v2/releases/rollout', {
        libraryApkId: Number(target.id),
        thingNames: chosen.map((x) => x.thing_name),
      });
      const res = (r.data && r.data.data) || {};
      const pass = res.first_pass || {};
      const failedN = res.failed_count || 0;
      if ((res.recorded_count || 0) === 0) {
        setMsg({ tone: 'bad', text: 'Nothing was recorded. ' +
          ((res.failures || []).map((f) => f.thing_name + ': ' + f.error).join('; ') || 'No reason returned.') });
        setBusy(false);
        return;
      }
      // Report every outcome the pass returned, not a hand-picked three.
      //
      // This used to print issued / waiting / satisfied and nothing else, so a
      // pass that only VERIFIED read "0 sent now, 0 waiting, 0 already on it" —
      // which is what "nothing happened" looks like, for real work. verified is
      // the commonest outcome of all: an agent self-install cannot report its own
      // result (the process is killed replacing its own APK), so the next pass
      // asks the cab what it is running instead of installing again. `abandoned`
      // was missing too, which is worse — a cab that has given up was simply
      // absent from the sentence.
      //
      // Counts here can EXCEED the number recorded, and that is correct: the pass
      // covers all pending work on the cabs you chose, not only what this click
      // created. Rolling the agent to seven cabs that still had a player install
      // pending returned fourteen outcomes for seven intents. The old wording had
      // no way to say that, so it is said plainly instead of silently implying
      // the numbers should add up.
      const outcomes = [];
      if (pass.issued) outcomes.push(pass.issued + ' sent now');
      if (pass.verified) outcomes.push(pass.verified + ' installed already, asking the cab to confirm');
      if (pass.satisfied) outcomes.push(pass.satisfied + ' confirmed on it');
      if (pass.waiting) outcomes.push(pass.waiting + ' waiting for the cab to come back');
      if (pass.abandoned) outcomes.push(pass.abandoned + ' given up on');
      if (pass.errors) outcomes.push(pass.errors + ' errored');
      const recordedN = (res.requested || []).filter((x) => x.recorded).length;
      onDone({
        tone: (failedN > 0 || pass.abandoned || pass.errors) ? 'warn' : 'grow',
        text: recordedN + ' intent(s) recorded for '
            + pkgShort(res.package_name) + ' vc' + res.target_version_code
            + ' — retried until taken or the intent expires ('
            + Math.round((res.expires_in_hours || 0) / 24) + ' days). '
            + (outcomes.length
                ? 'This pass, across all pending work on those cabs: ' + outcomes.join(', ') + '.'
                : 'Nothing was due on those cabs this pass.')
            + (failedN > 0 ? ' ' + failedN + ' could NOT be recorded: '
               + (res.failures || []).map((f) => f.thing_name).join(', ') + '.' : '')
            // H0 (A19 / C14): what this rollout will pull over the cabs' SIMs — file_size × devices,
            // the server's figure. A 413 (over the per-device budget without confirmLarge) lands in
            // the catch below with the server's sentence.
            + (res.sim_budget_bytes ? ' About ' + Math.round(res.sim_budget_bytes / (1024 * 1024))
               + ' MB will cross the SIMs for this rollout.' : ''),
      });
    } catch (e) {
      setMsg({ tone: 'warn', text: (e && e.response && e.response.data && e.response.data.error) || e.message || 'Rollout failed' });
      setBusy(false);
    }
  };

  return (
    <Drawer
      eyebrow="Release"
      title={'Roll out to ' + chosen.length + ' device' + (chosen.length === 1 ? '' : 's')}
      sub="Records an intent per device. Reachable cabs are asked now; the rest are retried when they return."
      onClose={onClose}
      footer={<Button primary onClick={send} disabled={busy || !target || chosen.length === 0}>
        {busy ? 'Sending…' : 'Send to ' + chosen.length}
      </Button>}
    >
      <div className={PANE}>
        {msg && <div style={note(msg.tone)}>{msg.text}</div>}

        <RvCard title="What to send">
          <RvRow label="Build">
            <select style={{ ...input, width: '100%' }} value={targetId}
                    onChange={(e) => setTargetId(e.target.value)} aria-label="build to send">
              <option value="">Choose a library build…</option>
              {library.filter((l) => l.version_code != null).map((l) => (
                <option key={l.id} value={l.id}>
                  {pkgShort(l.package_name)} {l.version_name} (vc{l.version_code})
                  {l.sha256 ? ' ✓ verifiable' : ' — no checksum'}
                </option>
              ))}
            </select>
          </RvRow>
          {target && (
            <RvRow label="Verification"
                   hint={target.sha256
                     ? 'The device hashes what it downloaded and refuses with CHECKSUM_MISMATCH if it does not match.'
                     : 'This build predates V381, so the device has nothing to check the bytes against.'}>
              {target.sha256
                ? <span style={{ color: 'var(--grow-text)' }}>checksum will be sent</span>
                : <span style={{ color: 'var(--warn-text)' }}>unverified install</span>}
            </RvRow>
          )}
        </RvCard>

        <RvCard title="Who it goes to">
          <RvRow label="Reachable now" hint="asked immediately">{reachable}</RvRow>
          <RvRow label="Asleep" hint="power-idle — asked when the cab wakes. Not a reason to leave it out; this is what the retry is for.">{asleep}</RvRow>
          <RvRow label="Not reporting" hint="silent for over 30 minutes — these wait, and the intent expires if the cab never returns.">{silent}</RvRow>
          {alreadyOn > 0 && (
            <RvRow label="Already on it" hint="these resolve without an install">{alreadyOn}</RvRow>
          )}
        </RvCard>

        {chosen.length > 0 && (
          <RvCard title="Devices">
            <div style={{ fontSize: 12.5, fontFamily: 'ui-monospace, monospace', lineHeight: 1.7 }}>
              {chosen.map((x) => (
                <div key={x.thing_name}>
                  {x.thing_name.replace('ggos-xx-', '')} · vc{x.agent_code == null ? '?' : x.agent_code} · {powerState(x).label}
                </div>
              ))}
            </div>
          </RvCard>
        )}
      </div>
    </Drawer>
  );
}

/* ── Drawer 3: the archive ──────────────────────────────────────────────── */
// Install attempts, build history and the library are consulted, not worked in.
// Sections inside one drawer, rather than three tables under the register.
function HistoryDrawer({ rollouts, builds, library, onClose }) {
  return (
    <EntityDrawer
      eyebrow="Release"
      title="History"
      onClose={onClose}
      defaultSection="attempts"
      sections={[
        { key: 'attempts', label: 'Install attempts', Icon: ListChecks, render: () => (
          <div style={SECPAD}>
            <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--ink-soft)' }}>
              <strong>confirmed</strong>: the device reported it, or the version was later observed on it ·{' '}
              <strong>unreported</strong>: published, nothing came back — not the same as failed ·{' '}
              <strong>failed</strong>: the device said no, or the publish threw.
            </p>
            {rollouts.length === 0 ? <Empty title="Nothing pushed yet">No install attempts recorded.</Empty> : (
              <DataTable
                rows={rollouts} getKey={(r) => r.command_id}
                filterOn={['thing_name', 'intended_version', 'issued_by', 'outcome']}
                filterPlaceholder="Filter by device, version, who issued it…"
                initialSort={{ key: 'issued_at', dir: 'desc' }}
                columns={[
                  { key: 'outcome', label: 'Outcome', width: 108,
                    render: (r) => <Pill tone={OUTCOME_TONE[r.outcome] || 'pend'}>{r.outcome}</Pill>,
                    sort: (r) => ({ failed: 0, unreported: 1, unconfirmed: 2, confirmed: 3 }[r.outcome] == null ? 4 : { failed: 0, unreported: 1, unconfirmed: 2, confirmed: 3 }[r.outcome]) },
                  { key: 'thing_name', label: 'Device', mono: true },
                  { key: 'intended_version', label: 'Intended', mono: true,
                    render: (r) => (r.version_known ? r.intended_version + ' (vc' + r.intended_code + ')'
                      : <span style={{ color: 'var(--warn-text)' }}>version unknown</span>) },
                  { key: 'attested', label: 'Evidence', width: 140, sortable: false,
                    render: (r) => {
                      if (r.attested) return <span style={{ color: 'var(--grow-text)' }}>observed installed</span>;
                      if (r.install_status === 'session_committed') return <span style={{ color: 'var(--ink-soft)' }}>session committed</span>;
                      if (r.status === 'executed') return <span style={{ color: 'var(--ink-soft)' }}>device reported</span>;
                      return <span style={{ color: 'var(--ink-faint)' }}>—</span>;
                    } },
                  { key: 'issued_at', label: 'Issued', width: 132, render: (r) => fmt(r.issued_at) },
                  { key: 'issued_by', label: 'By' },
                ]}
              />
            )}
          </div>
        ) },
        { key: 'builds', label: 'Builds', Icon: Hammer, render: () => (
          <div style={SECPAD}>
            {builds.length === 0 ? <Empty title="No builds">Nothing built yet.</Empty> : (
              <DataTable
                rows={builds} getKey={(r) => r.id}
                filterOn={['branch', 'status', 'triggered_by', 'git_commit']}
                filterPlaceholder="Filter by branch, status, who started it…"
                initialSort={{ key: 'created_at', dir: 'desc' }}
                columns={[
                  { key: 'status', label: 'Status', width: 92,
                    render: (r) => <Pill tone={BUILD_TONE[r.status] || 'pend'}>{r.status}</Pill> },
                  { key: 'branch', label: 'Branch', mono: true },
                  { key: 'git_commit', label: 'Commit', mono: true,
                    render: (r) => (r.git_commit ? String(r.git_commit).slice(0, 9) : '—') },
                  { key: 'landed', label: 'Landed', width: 120,
                    render: (r) => (r.landed
                      ? r.landed + ' apk' + (r.landed > 1 ? 's' : '') + (r.landed_with_checksum === r.landed ? ' ✓sha' : ' (' + r.landed_with_checksum + ' sha)')
                      : '—') },
                  { key: 'created_at', label: 'Started', width: 132, render: (r) => fmt(r.created_at) },
                  { key: 'error_message', label: 'Why', wrap: true,
                    render: (r) => (r.error_message ? String(r.error_message).slice(0, 160) : '—') },
                ]}
              />
            )}
          </div>
        ) },
        { key: 'library', label: 'Library', Icon: Library, render: () => (
          <div style={SECPAD}>
            {library.length === 0 ? <Empty title="No builds">Nothing in the library.</Empty> : (
              <DataTable
                rows={library} getKey={(r) => r.id}
                filterOn={['package_name', 'version_name', 'branch', 'git_commit']}
                filterPlaceholder="Filter by package, version, branch, commit…"
                initialSort={{ key: 'uploaded_at', dir: 'desc' }}
                columns={[
                  { key: 'version_name', label: 'Version', mono: true,
                    render: (r) => r.version_name + ' (vc' + r.version_code + ')' },
                  { key: 'package_name', label: 'Package', mono: true, render: (r) => pkgShort(r.package_name) },
                  { key: 'sha256', label: 'Verifiable', width: 104,
                    render: (r) => (r.sha256
                      ? <span title={r.sha256} style={{ color: 'var(--grow-text)' }}>✓ checksum</span>
                      : <span style={{ color: 'var(--ink-faint)' }} title="predates V381 — installs unverified">—</span>) },
                  { key: 'install_count', label: 'Installs', width: 76 },
                  { key: 'uploaded_at', label: 'Built', width: 132, render: (r) => fmt(r.uploaded_at) },
                ]}
              />
            )}
          </div>
        ) },
      ]}
    />
  );
}

/* ── The register ───────────────────────────────────────────────────────── */
export default function Releases() {
  const { data, error, loading, reload } = useApi(() => api.get('/v2/releases').then((r) => r.data), []);
  const builds = useApi(() => api.get('/v2/releases/builds').then((r) => r.data), []);
  const intents = useApi(() => api.get('/v2/releases/intents').then((r) => r.data), []);

  const [sel, setSel] = useState(() => new Set());
  const [drawer, setDrawer] = useState(null);   // 'build' | 'rollout' | 'history'
  const [flash, setFlash] = useState(null);

  const d = (data && data.data) || {};
  const p = d.pulse || {};
  const cap = d.capability || {};
  const rollouts = useMemo(() => d.rollouts || [], [d.rollouts]);
  const library = useMemo(() => d.library || [], [d.library]);
  const buildRows = useMemo(
    () => (builds.data && builds.data.data && builds.data.data.rows) || [], [builds.data]);
  const newest = p.newest_in_field;

  // One row per device, carrying everything about it — version, power and any
  // outstanding intent. This join IS the screen: "still on vc33 · asleep 9h ·
  // pending vc35, 2 tries, DNS" is one sentence, not three tables.
  const byThing = useMemo(() => {
    const m = new Map();
    const rows = (intents.data && intents.data.data && intents.data.data.rows) || [];
    for (const r of rows) if (r.state === 'pending' && !m.has(r.thing_name)) m.set(r.thing_name, r);
    return m;
  }, [intents.data]);

  const devices = useMemo(
    () => (d.devices || []).map((x) => ({ ...x, intent: byThing.get(x.thing_name) || null })),
    [d.devices, byThing]);

  const toggle = useCallback((t) => setSel((prev) => {
    const n = new Set(prev); if (n.has(t)) n.delete(t); else n.add(t); return n;
  }), []);

  const refreshAll = useCallback(() => { reload(); builds.reload(); intents.reload(); },
    [reload, builds, intents]);

  const head = <PageHead title="Releases" sub="What is running where, and whether the last push landed." />;
  if (loading) return <>{head}<Loading what="the fleet's versions" /></>;
  if (error) return <>{head}<ErrorState error={error} onRetry={reload} /></>;

  const asleep = devices.filter((x) => x.power_idle).length;
  const draining = devices.filter((x) => x.draining_unplugged).length;
  const unknown = devices.filter((x) => !x.state_at).length;
  const outstanding = byThing.size;

  return (
    <>
      <PageHead
        title="Releases"
        sub="What is running where, and whether the last push landed."
        actions={
          <>
            <Button onClick={() => setDrawer('build')}>Build…</Button>
            <Button onClick={() => setDrawer('history')}>History…</Button>
            <Button primary onClick={() => setDrawer('rollout')} disabled={sel.size === 0}>
              {sel.size ? 'Roll out (' + sel.size + ')' : 'Roll out'}
            </Button>
            <Button onClick={refreshAll}>Refresh</Button>
          </>
        }
      />

      {cap.note && <div style={note('warn')}>{cap.note}</div>}
      {flash && <div style={note(flash.tone)}>{flash.text}</div>}

      {draining > 0 && (
        <div style={note('bad')}>
          <strong>{draining} device{draining > 1 ? 's are' : ' is'} awake, unplugged and under 20%.</strong>{' '}
          Before agent 1.6.11 a woken device never re-armed its sleep timer, so it stayed awake until it ran
          flat — 60 of 66 such wakes ended in a hard power-off, after which the cab must be started to
          recover. This is the one power state worth acting on.
        </div>
      )}

      <Tiles items={[
        { label: 'Devices', value: p.devices == null ? '—' : p.devices, foot: 'active, non-test' },
        { label: 'Newest in field', value: newest != null ? 'vc' + newest : '—', foot: 'no target is declared' },
        { label: 'On newest', value: p.on_newest == null ? '—' : p.on_newest, tone: 'grow' },
        { label: 'Behind', value: p.behind == null ? '—' : p.behind, tone: p.behind ? 'warn' : undefined },
        { label: 'Asleep', value: asleep, foot: 'power-idle — not a fault' },
        { label: 'Draining', value: draining, tone: draining ? 'bad' : undefined, foot: 'woken, unplugged, <20%' },
        { label: 'Outstanding', value: outstanding, tone: outstanding ? 'warn' : undefined, foot: 'intents awaiting a cab' },
        { label: 'Failed', value: p.failed == null ? '—' : p.failed, tone: p.failed ? 'bad' : undefined, foot: 'device said no' },
      ]} />

      <p style={{ margin: '18px 0 10px', fontSize: 12.5, color: 'var(--ink-soft)' }}>
        Versions are as last <em>reported</em> — <code>ggos_device_apps</code> refreshes only when a package
        list is requested, so a row can be older than the device. “Behind” is measured against the newest
        version in the field; no target is declared, because one would mark a device that cannot be upgraded
        as permanently overdue. Power is from the last 30 minutes
        {unknown > 0 && <>; <strong>{unknown}</strong> device{unknown > 1 ? 's have' : ' has'} not reported in
        that window and read <em>unknown</em> rather than a guess</>}. Select rows, then Roll out.
      </p>

      {devices.length === 0
        ? <Empty title="No active devices">Nothing to show.</Empty>
        : (
          <DataTable
            rows={devices}
            getKey={(r) => r.thing_name}
            filterOn={['thing_name', 'device_model', 'agent_version']}
            filterPlaceholder="Filter by thing name, model, version…"
            initialSort={{ key: 'agent_code', dir: 'asc' }}
            right={sel.size > 0 ? <Button onClick={() => setSel(new Set())}>Clear {sel.size}</Button> : null}
            columns={[
              // Select-all in the header. The previous cut had a bulk button and
              // the refactor dropped it, which made the one action this screen
              // exists for a click per cab. Capped at the server's own 250 per
              // request so the UI cannot arm a batch the endpoint will refuse.
              { key: 'pick', width: 34, sortable: false,
                label: (
                  <input type="checkbox" aria-label="select all devices"
                         checked={sel.size > 0 && sel.size >= Math.min(devices.length, ROLLOUT_CAP)}
                         ref={(el) => { if (el) el.indeterminate = sel.size > 0 && sel.size < Math.min(devices.length, ROLLOUT_CAP); }}
                         onChange={(e) => setSel(e.target.checked
                           ? new Set(devices.slice(0, ROLLOUT_CAP).map((x) => x.thing_name))
                           : new Set())} />
                ),
                render: (r) => (
                  <input type="checkbox" aria-label={'select ' + r.thing_name}
                         checked={sel.has(r.thing_name)} onChange={() => toggle(r.thing_name)} />
                ) },
              { key: 'agent_code', label: 'Agent', width: 96,
                render: (r) => (r.agent_code == null
                  ? <Pill tone="warn">unknown</Pill>
                  : <Pill tone={newest != null && Number(r.agent_code) < newest ? 'warn' : 'grow'}>vc{r.agent_code}</Pill>),
                sort: (r) => (r.agent_code == null ? -1 : Number(r.agent_code)) },
              { key: 'thing_name', label: 'Device', mono: true,
                render: (r) => (
                  <Link to={'/v2/fleet/device/' + encodeURIComponent(r.thing_name)} style={{ color: 'var(--iris-text)' }}>
                    {r.thing_name}
                  </Link>
                ) },
              { key: 'power', label: 'Power', width: 146, sortable: false,
                render: (r) => { const st = powerState(r); return <span title={st.why}><Pill tone={st.tone}>{st.label}</Pill></span>; } },
              // The adjacency that answers "why is this one still behind".
              { key: 'intent', label: 'Outstanding', width: 206, sortable: false,
                render: (r) => {
                  if (!r.intent) return <span style={{ color: 'var(--ink-faint)' }}>—</span>;
                  const i = r.intent;
                  return (
                    <span title={i.last_error || ('requested by ' + i.requested_by)}>
                      <Pill tone="warn">vc{i.target_version_code}</Pill>{' '}
                      <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                        {i.attempts} {Number(i.attempts) === 1 ? 'try' : 'tries'}
                        {i.last_error ? ' · ' + String(i.last_error).slice(0, 26) + '…' : ''}
                      </span>
                    </span>
                  );
                } },
              { key: 'device_model', label: 'Hardware',
                render: (r) => (r.device_model || '—') + ' / Android ' + (r.android_version || '?') },
              { key: 'agent_synced_at', label: 'Reported', width: 132, render: (r) => fmt(r.agent_synced_at) },
              { key: 'connection_status', label: 'Link', width: 84,
                render: (r) => (
                  <Pill tone={r.connection_status === 'online' ? 'grow' : 'pend'}>
                    {r.connection_status || 'unknown'}
                  </Pill>
                ) },
            ]}
          />
        )}

      {drawer === 'build' && (
        <BuildDrawer builds={builds} onClose={() => setDrawer(null)} onStarted={refreshAll} />
      )}
      {drawer === 'rollout' && (
        <RolloutDrawer
          selected={sel} devices={devices} library={library}
          onClose={() => setDrawer(null)}
          onDone={(f) => { setFlash(f); setSel(new Set()); setDrawer(null); refreshAll(); }}
        />
      )}
      {drawer === 'history' && (
        <HistoryDrawer rollouts={rollouts} builds={buildRows} library={library} onClose={() => setDrawer(null)} />
      )}
    </>
  );
}
